/**
 * Utilities for calculating comparison periods and percentage changes
 * for period-over-period reporting.
 */

export interface ComparisonPeriod {
  start: Date;
  end: Date;
  label: string;  // "vs yesterday", "vs last week", etc.
}

export interface ChangeResult {
  percent: number;
  direction: 'up' | 'down' | 'neutral';
}

/**
 * Calculate the comparison period based on the current date range.
 * The comparison period has the same duration and ends the day before the current period starts.
 * 
 * Examples:
 * - Today → Yesterday
 * - This week (7 days) → Last week (previous 7 days)
 * - Last 30 days → Previous 30 days before that
 */
export function getComparisonPeriod(startDate: Date, endDate: Date): ComparisonPeriod {
  // Calculate duration of current period in days
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationDays = Math.round(durationMs / (1000 * 60 * 60 * 24)) + 1;
  
  // Previous period ends the day before current starts
  const end = new Date(startDate);
  end.setDate(end.getDate() - 1);
  // Set to end of day
  end.setHours(23, 59, 59, 999);
  
  // Previous period has the same duration
  const start = new Date(end);
  start.setDate(start.getDate() - durationDays + 1);
  // Set to start of day
  start.setHours(0, 0, 0, 0);
  
  // Generate human-readable label
  let label: string;
  if (durationDays === 1) {
    label = 'vs yesterday';
  } else if (durationDays === 7) {
    label = 'vs last week';
  } else if (durationDays >= 28 && durationDays <= 31) {
    label = 'vs last month';
  } else if (durationDays >= 89 && durationDays <= 92) {
    label = 'vs last quarter';
  } else {
    label = `vs prev ${durationDays}d`;
  }
  
  return { start, end, label };
}

/**
 * Calculate percentage change between current and previous values.
 * Returns direction and absolute percentage.
 */
export function calculateChange(current: number, previous: number): ChangeResult {
  // Both zero - no change
  if (previous === 0 && current === 0) {
    return { percent: 0, direction: 'neutral' };
  }
  
  // Previous was zero but current has value - 100% increase
  if (previous === 0) {
    return { percent: 100, direction: 'up' };
  }
  
  // Calculate percentage change
  const percentChange = ((current - previous) / Math.abs(previous)) * 100;
  
  // Use 0.5% threshold for neutral to avoid showing tiny fluctuations
  if (Math.abs(percentChange) < 0.5) {
    return { percent: 0, direction: 'neutral' };
  }
  
  return {
    percent: Math.abs(percentChange),
    direction: percentChange > 0 ? 'up' : 'down',
  };
}

/**
 * Format a comparison result for display
 */
export function formatComparison(change: ChangeResult, label: string): string {
  if (change.direction === 'neutral') {
    return `No change ${label}`;
  }
  
  const arrow = change.direction === 'up' ? '↑' : '↓';
  return `${arrow} ${change.percent.toFixed(1)}% ${label}`;
}
